import type { EventHook } from '@vueuse/core';
import type { Ref } from 'vue';
import type { BlockSerialized } from '#layers/block-engine/types/block';
import type { BlockChangeEvent } from '#layers/block-engine/types/block-editor';
import { computed, nextTick } from 'vue';

export function useBlockUpdate({
	blockData,
	changeEvent,
}: {
	blockData: Ref<BlockSerialized | null>;
	changeEvent: EventHook<BlockChangeEvent>;
}) {
	/**
	 * Update the edited block
	 */
	function updateBlock(changes: Partial<BlockSerialized>) {
		if (!blockData.value) {
			return;
		}

		blockData.value = {
			...blockData.value,
			...changes,
		};

		changeEvent.trigger({
			block: blockData.value,
			changes,
		});
	}

	// Allow multiple child props updates in the same tick
	// to avoid updating only the last prop
	// because the `children` data is cloned each time
	const childPropUpdateBuffer = new Map<string, Record<string, any>>();
	let pendingChildPropUpdate = false;

	function updateChildProps() {
		if (pendingChildPropUpdate) {
			return;
		}

		pendingChildPropUpdate = true;

		nextTick(() => {
			if (blockData.value) {
				const children = {
					...blockData.value.children,
				};

				for (const [childId, props] of childPropUpdateBuffer) {
					if (children[childId] && props) {
						children[childId] = {
							...children[childId],
							props: {
								...children[childId].props,
								...props,
							},
						};
					}
				}

				childPropUpdateBuffer.clear();

				updateBlock({
					children,
				});
			}

			pendingChildPropUpdate = false;
		});
	}

	/**
	 * Update the prop of a child block
	 */
	function updateChildProp(childId: string, propKey: string, value: any) {
		if (!childPropUpdateBuffer.has(childId)) {
			childPropUpdateBuffer.set(childId, {});
		}

		childPropUpdateBuffer.get(childId)![propKey] = value;
		updateChildProps();
	}

	// Useful for v-model
	function useBlockModel<TKey extends keyof BlockSerialized>(key: TKey) {
		return computed({
			get: () => blockData.value?.[key],
			set: (value) => {
				updateBlock({
					[key]: value,
				});
			},
		});
	}

	return {
		updateBlock,
		updateChildProp,
		useBlockModel,
	};
}

export type BlockEditorUpdateBlock = ReturnType<typeof useBlockUpdate>['updateBlock'];
